/*
 * Copyright 2025 Mia srl
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Analytics Service
 * Tracks user behavior and application events
 */

import { v4 as uuidv4 } from 'uuid';
import config from '../config';
import { getBffHeaders, buildBffUrl } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

class AnalyticsService {
  constructor() {
    this.baseUrl = config.bffUrl;
    this.sessionId = null;
    this.eventQueue = [];
    this.flushInterval = null;
    this.isEnabled = true;
    this.batchSize = 10;
    this.flushIntervalMs = 5000; // Flush every 5 seconds
    this.sessionStartTime = null;
  }

  /**
   * Initialize analytics tracking
   */
  initialize() {
    // Check if user has opted out of analytics
    const optOut = localStorage.getItem('analytics_opt_out');
    if (optOut === 'true') {
      this.isEnabled = false;
      console.log('📊 Analytics tracking disabled by user preference');
      return;
    }

    // Generate or retrieve session ID
    this.sessionId = sessionStorage.getItem('analytics_session_id');
    if (!this.sessionId) {
      this.sessionId = uuidv4();
      sessionStorage.setItem('analytics_session_id', this.sessionId);
    }

    // Start session
    this.startSession();

    // Set up periodic flush
    this.flushInterval = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.endSession();
      this.flush(true); // Force sync flush
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden');
      } else {
        this.trackEvent('page_visible');
      }
    });

    console.log('📊 Analytics tracking initialized', { sessionId: this.sessionId });
  }

  /**
   * Start a new session
   */
  async startSession() {
    if (!this.isEnabled) return;

    this.sessionStartTime = Date.now();

    const metadata = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const url = buildBffUrl('/api/analytics/session/start');
      await getAuthorizedFetch()(url, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          sessionId: this.sessionId,
          metadata,
        }),
      });
    } catch (error) {
      console.error('Failed to start analytics session:', error);
    }
  }

  /**
   * End the current session
   */
  async endSession() {
    if (!this.isEnabled || !this.sessionId) return;

    try {
      const url = buildBffUrl('/api/analytics/session/end');
      await getAuthorizedFetch()(url, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          sessionId: this.sessionId,
        }),
      });
    } catch (error) {
      console.error('Failed to end analytics session:', error);
    }
  }

  /**
   * Get current page context
   * @returns {Object} Page context information
   */
  getPageContext() {
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    
    return {
      pathname,
      pageName: this.getPageName(pathname),
      conversationId: searchParams.get('conversationId'),
      projectId: searchParams.get('projectId'),
      referrer: document.referrer,
      title: document.title,
    };
  }

  /**
   * Get friendly page name from pathname
   * @param {string} pathname - URL pathname
   * @returns {string} Friendly page name
   */
  getPageName(pathname) {
    const routes = {
      '/': 'home',
      '/chat': 'chat',
      '/projects': 'projects',
      '/templates': 'templates',
      '/learn': 'learn',
      '/settings': 'settings',
      '/tools': 'tools',
      '/settings/theme': 'settings_theme',
      '/settings/advanced': 'settings_advanced',
      '/settings/analytics': 'settings_analytics',
      '/settings/privacy': 'settings_privacy',
      '/settings/billing': 'settings_billing',
    };
    
    return routes[pathname] || pathname.replace(/\//g, '_').substring(1) || 'unknown';
  }

  /**
   * Track an event
   * @param {string} eventType - Type of event
   * @param {Object} properties - Event properties
   */
  trackEvent(eventType, properties = {}) {
    if (!this.isEnabled) return;

    const pageContext = this.getPageContext();
    
    const event = {
      eventType,
      sessionId: this.sessionId,
      properties: {
        ...properties,
        pageName: pageContext.pageName,
        conversationId: pageContext.conversationId,
        projectId: pageContext.projectId,
      },
      timestamp: new Date().toISOString(),
      url: window.location.href,
      pathname: window.location.pathname,
    };

    this.eventQueue.push(event);

    // Flush if queue is full
    if (this.eventQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   * @param {Object} properties - Additional properties
   */
  trackPageView(pageName, properties = {}) {
    this.trackEvent('page_view', {
      pageName,
      ...properties,
    });
  }

  /**
   * Track button click
   * @param {string} buttonName - Name of the button
   * @param {Object} properties - Additional properties
   */
  trackClick(buttonName, properties = {}) {
    this.trackEvent('button_click', {
      buttonName,
      ...properties,
    });
  }

  /**
   * Track conversation event
   * @param {string} action - Action type (create, update, delete, etc.)
   * @param {Object} properties - Additional properties
   */
  trackConversation(action, properties = {}) {
    this.trackEvent('conversation', {
      action,
      ...properties,
    });
  }

  /**
   * Track message event
   * @param {string} action - Action type (send, receive, edit, etc.)
   * @param {Object} properties - Additional properties
   */
  trackMessage(action, properties = {}) {
    this.trackEvent('message', {
      action,
      ...properties,
    });
  }

  /**
   * Track tool/function call
   * @param {string} toolName - Name of the tool
   * @param {Object} properties - Additional properties
   */
  trackToolCall(toolName, properties = {}) {
    this.trackEvent('tool_call', {
      toolName,
      ...properties,
    });
  }

  /**
   * Track error
   * @param {string} errorType - Type of error
   * @param {Object} properties - Error details
   */
  trackError(errorType, properties = {}) {
    this.trackEvent('error', {
      errorType,
      ...properties,
    });
  }

  /**
   * Track feature usage
   * @param {string} featureName - Name of the feature
   * @param {Object} properties - Additional properties
   */
  trackFeature(featureName, properties = {}) {
    this.trackEvent('feature_usage', {
      featureName,
      ...properties,
    });
  }

  /**
   * Track authentication event
   * @param {string} action - Action type (login, logout, etc.)
   * @param {Object} properties - Additional properties
   */
  trackAuth(action, properties = {}) {
    this.trackEvent('authentication', {
      action,
      ...properties,
    });
  }

  /**
   * Track navigation event
   * @param {string} from - Previous location
   * @param {string} to - New location
   * @param {Object} properties - Additional properties
   */
  trackNavigation(from, to, properties = {}) {
    this.trackEvent('navigation', {
      from,
      to,
      ...properties,
    });
  }

  /**
   * Track search event
   * @param {string} query - Search query
   * @param {Object} properties - Additional properties
   */
  trackSearch(query, properties = {}) {
    this.trackEvent('search', {
      query,
      ...properties,
    });
  }

  /**
   * Track performance metric
   * @param {string} metricName - Name of the metric
   * @param {number} value - Metric value
   * @param {Object} properties - Additional properties
   */
  trackPerformance(metricName, value, properties = {}) {
    this.trackEvent('performance', {
      metricName,
      value,
      ...properties,
    });
  }

  /**
   * Track project-related action
   * @param {string} action - Action performed (create, open, delete, etc.)
   * @param {Object} properties - Project properties
   */
  trackProject(action, properties = {}) {
    this.trackEvent('project_action', {
      action,
      projectType: properties.projectType,
      projectId: properties.projectId,
      projectName: properties.projectName,
      template: properties.template,
      tenantId: properties.tenantId,
      ...properties,
    });
  }

  /**
   * Track language/framework usage
   * @param {string} language - Programming language or framework
   * @param {Object} properties - Additional properties
   */
  trackLanguage(language, properties = {}) {
    this.trackEvent('language_usage', {
      language,
      action: properties.action || 'used',
      fileExtension: properties.fileExtension,
      linesOfCode: properties.linesOfCode,
      ...properties,
    });
  }

  /**
   * Track code generation
   * @param {string} language - Target language
   * @param {Object} properties - Generation properties
   */
  trackCodeGeneration(language, properties = {}) {
    this.trackEvent('code_generation', {
      language,
      success: properties.success,
      linesGenerated: properties.linesGenerated,
      prompt: properties.prompt,
      duration: properties.duration,
      ...properties,
    });
  }

  /**
   * Track action performed
   * @param {string} category - Action category (file, ui, api, etc.)
   * @param {string} action - Specific action
   * @param {Object} properties - Additional properties
   */
  trackAction(category, action, properties = {}) {
    this.trackEvent('user_action', {
      category,
      action,
      target: properties.target,
      value: properties.value,
      ...properties,
    });
  }

  /**
   * Track file operation
   * @param {string} operation - Operation type (create, edit, delete, save)
   * @param {Object} properties - File properties
   */
  trackFile(operation, properties = {}) {
    this.trackEvent('file_operation', {
      operation,
      fileName: properties.fileName,
      fileType: properties.fileType,
      fileSize: properties.fileSize,
      language: properties.language,
      ...properties,
    });
  }

  /**
   * Track AI interaction
   * @param {string} interactionType - Type of interaction (prompt, response, etc.)
   * @param {Object} properties - Interaction properties
   */
  trackAIInteraction(interactionType, properties = {}) {
    this.trackEvent('ai_interaction', {
      interactionType,
      model: properties.model,
      promptLength: properties.promptLength,
      responseLength: properties.responseLength,
      tokensUsed: properties.tokensUsed,
      duration: properties.duration,
      success: properties.success,
      ...properties,
    });
  }

  /**
   * Track template usage
   * @param {string} templateId - Template identifier
   * @param {Object} properties - Template properties
   */
  trackTemplate(templateId, properties = {}) {
    this.trackEvent('template_usage', {
      templateId,
      templateName: properties.templateName,
      templateCategory: properties.templateCategory,
      action: properties.action || 'applied',
      ...properties,
    });
  }

  /**
   * Track deployment action
   * @param {string} action - Deployment action (start, complete, fail)
   * @param {Object} properties - Deployment properties
   */
  trackDeployment(action, properties = {}) {
    this.trackEvent('deployment', {
      action,
      environment: properties.environment,
      projectId: properties.projectId,
      duration: properties.duration,
      success: properties.success,
      ...properties,
    });
  }

  /**
   * Track integration usage
   * @param {string} integration - Integration name (github, gitlab, etc.)
   * @param {string} action - Action performed
   * @param {Object} properties - Additional properties
   */
  trackIntegration(integration, action, properties = {}) {
    this.trackEvent('integration', {
      integration,
      action,
      success: properties.success,
      ...properties,
    });
  }

  /**
   * Flush queued events to the server
   * @param {boolean} sync - Whether to flush synchronously
   */
  async flush(sync = false) {
    if (!this.isEnabled || this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Use fetch with keepalive for both sync and async sends
      // This ensures we can include the API key header even on page unload
      const url = buildBffUrl('/api/analytics/events/batch');
      await getAuthorizedFetch()(url, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ events: eventsToSend }),
        keepalive: sync, // keepalive ensures the request completes even if page unloads
      });
    } catch (error) {
      console.error('Failed to send analytics events:', error);
      // Re-queue events on failure
      this.eventQueue.unshift(...eventsToSend);
    }
  }

  /**
   * Enable analytics tracking
   */
  enable() {
    this.isEnabled = true;
    localStorage.removeItem('analytics_opt_out');
    console.log('📊 Analytics tracking enabled');
    this.initialize();
  }

  /**
   * Disable analytics tracking
   */
  disable() {
    this.isEnabled = false;
    localStorage.setItem('analytics_opt_out', 'true');
    console.log('📊 Analytics tracking disabled');
    
    // Clear session
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    // End session
    this.endSession();
    
    // Clear queued events
    this.eventQueue = [];
  }

  /**
   * Check if analytics is enabled
   */
  isAnalyticsEnabled() {
    return this.isEnabled;
  }

  /**
   * Get session duration
   */
  getSessionDuration() {
    if (!this.sessionStartTime) return 0;
    return Date.now() - this.sessionStartTime;
  }

  /**
   * Fetch token usage metrics
   * @param {string} range - Time range: 'day', 'month', or 'year'
   * @param {Date} date - The date to fetch data for (optional, defaults to current date)
   * @returns {Promise<Object>} Token usage data
   */
  async getTokenUsage(range = 'month', date = new Date()) {
    try {
      // Get user info from localStorage to include in request
      const userInfo = JSON.parse(localStorage.getItem('mia_flow_user_info') || '{}');
      const userId = userInfo.sub || userInfo.email || 'anonymous';
      
      const additionalHeaders = {
        'Content-Type': 'application/json',
      };
      
      // Add user ID header for analytics filtering
      if (userId && userId !== 'anonymous') {
        additionalHeaders['mia-userid'] = userId;
      }
      
      // Build URL - handle both absolute and relative bffUrl
      const queryParams = { range };
      if (date) {
        queryParams.date = date.toISOString();
      }
      const url = buildBffUrl('/api/analytics/token-usage', queryParams);
      
      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers: getBffHeaders(additionalHeaders),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch token usage: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching token usage:', error);
      throw error;
    }
  }

  /**
   * Fetch token usage metrics for a custom date range
   * @param {string} fromDate - Start date (ISO format)
   * @param {string} toDate - End date (ISO format)
   * @returns {Promise<Object>} Token usage data
   */
  async getTokenUsageByDateRange(fromDate, toDate) {
    try {
      // Get user info from localStorage to include in request
      const userInfo = JSON.parse(localStorage.getItem('mia_flow_user_info') || '{}');
      const userId = userInfo.sub || userInfo.email || 'anonymous';
      
      const additionalHeaders = {
        'Content-Type': 'application/json',
      };
      
      // Add user ID header for analytics filtering
      if (userId && userId !== 'anonymous') {
        additionalHeaders['mia-userid'] = userId;
      }
      
      // Build URL - handle both absolute and relative bffUrl
      const url = buildBffUrl('/api/analytics/token-usage', {
        from: fromDate,
        to: toDate
      });
      
      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers: getBffHeaders(additionalHeaders),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch token usage: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching token usage:', error);
      throw error;
    }
  }

  /**
   * Fetch pricing information
   * @returns {Promise<Object>} Pricing data
   */
  async getPricing() {
    try {
      const url = buildBffUrl('/api/analytics/pricing');
      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch pricing: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching pricing:', error);
      throw error;
    }
  }

  /**
   * Clear all analytics data (for privacy)
   */
  clearData() {
    this.eventQueue = [];
    sessionStorage.removeItem('analytics_session_id');
    console.log('📊 Analytics data cleared');
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
